// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { NotificationConfigurationInput } from '@/account/api/external-users';
import { MESSAGE_TYPES, messageTypeSchema } from '@/constants/message-type';
import {
  NotificationChannel,
  notificationChannelSchema,
} from '@/constants/notification-channel';
import { z } from 'zod';

const subscribedNotificationSchema = z.object({
  messageType: messageTypeSchema,
  activeChannels: z.array(notificationChannelSchema),
});

/**
 * Reads back what an external user is subscribed to, as the rows the dialog
 * draws.
 *
 * The dialog draws one row per message type it knows and binds each by
 * position, so this answers with that same list in that same order: a type the
 * user is not subscribed to gets a row with no channels rather than no row.
 *
 * A subscription naming a type this bundle has never heard of has no row to sit
 * in, and carrying it into the form would fail validation on a save the reader
 * never touched, so it is left out and reported instead.
 */
export function toSubscribedNotifications(
  notifications: { messageType: string; activeChannels: string[] }[] | undefined
): NotificationConfigurationInput[] {
  const subscribedChannels = new Map<string, NotificationChannel[]>();
  for (const notification of notifications ?? []) {
    const result = subscribedNotificationSchema.safeParse(notification);
    if (result.success) {
      subscribedChannels.set(
        result.data.messageType,
        result.data.activeChannels
      );
      continue;
    }
    if (import.meta.env.MODE === 'development') {
      console.error('Unknown message type:', notification, result.error);
    }
    Sentry.captureException(
      Error('Unknown message type', { cause: notification }),
      { extra: { notification, error: result.error } }
    );
  }
  return MESSAGE_TYPES.map((messageType) => ({
    messageType,
    activeChannels: subscribedChannels.get(messageType) ?? [],
  }));
}
